import { clearValue, getValue, setValue, toggle, toggleVisible, valueHasChanged } from "#skyward_controls";
import { getClosestContentPanel, notifyResize, registerLoadDOMCallback } from "#skyward_window";
import { focusAfter } from "./skyward.base.tabbing";
import { toggleAll as checkboxGroupToggleAll } from "./skyward.ui.checkboxGroup";
import { addDecimalMask, addMask, removeMask } from "./skyward.ui.datamask";
import { getSearchParameters as dmseGetSearchParameters } from "./skyward.ui.distinctMultiSelectEEL.values";
import { getActionFunction } from "./skyward.ui.events";
import { alignTitles } from "./skyward.ui.fieldset";
import { clearValue as htmlEditorClearValue, isHtmlEditorElement } from "./skyward.ui.htmlEditor";
import { setValue as radioButtonGroupSetValue } from "./skyward.ui.radioButtonGroup";
import { clear as standardFilterViewerClear } from "./skyward.ui.standardFilterViewer";
import { displayValidationMessages } from "./skyward.ui.validation";

type BoolString = "true" | "false";

export interface DependentRule
{
    Id: string;
    Visibility?: BoolString;
    ReadOnly?: BoolString;
    Clear?: BoolString;
    Toggle?: BoolString;
    Value?: string;
    Mask?: string;
    showMask?: boolean;
    DependentValue?: string;
    CopyParentValue?: BoolString;
    ClearErrors?: string;
    Placeholder?: string;
    ValueFunction?: string;
    ValueFunctionArgs?: string;
    TriggerOnLoad?: BoolString;
    OnValue?: string;
    NotOnValue?: string;
    AnimateVisibilityChange?: BoolString;
    Refresh?: BoolString; // only set from client via requestDataHash.setupDependency
}

export interface DependentsData
{
    dependents: DependentRule[];
    triggerOnLoad: boolean;
    isInnerChildRecordQuickEntry: boolean;
}

const skyward = window.skyward;

registerLoadDOMCallback(function (this: JQuery<HTMLElement>)
{
    toggleEEL(this, ".codeEEL", ".codeEELValue");
    toggleEEL(this, ".tileViewEEL", ".tileViewEEL_value");
});
registerLoadDOMCallback(toggleRadioButton);
registerLoadDOMCallback(toggleDropDown);
registerLoadDOMCallback(toggleDropDownAlternative);
registerLoadDOMCallback(toggleCheckBox);
registerLoadDOMCallback(toggleAccountDimensionEEL);
registerLoadDOMCallback(toggleDateTimePickers);
registerLoadDOMCallback(toggleDistinctMultiSelectEEL);
registerLoadDOMCallback(toggleInputs);

function toggleRadioButton(this: JQuery<HTMLElement>)
{
    const context = this;
    context.find(".radioSet, .radioButtonGroup").each(function ()
    {
        const radioset = $(this);
        const inputs = radioset.find("input:radio");
        inputs.on("change", function ()
        {
            const input = $(this);
            doProcessDependents(context, input, true);
            toggleTab.apply(context);
        });

        const current = radioset.find("input:checked");
        doProcessDependents(context, current, false, true);
        toggleTab.apply(context);
    });
}

function toggleDropDown(this: JQuery<HTMLElement>)
{
    const context = this;
    context.find(".comboBoxSet").each(function ()
    {
        const comboBoxSet = $(this);
        if (!comboBoxSet.hasClass("DefaultComboBoxSet"))
        {
            comboBoxSet.on("change", function ()
            {
                const input = $(this).find('option:selected');
                doProcessDependents(context, input, true);
            });

            const current = comboBoxSet.find("option:selected");
            doProcessDependents(context, current, false, true);
        }
    });
}

function toggleDropDownAlternative(this: JQuery<HTMLElement>)
{
    const context = this;
    context.find(".clearSpecialDependents").each(function ()
    {
        const comboBox = $(this);
        comboBox.on("change", function ()
        {
            doProcessDependents(context, comboBox, true);
        });

        doProcessDependents(context, comboBox, false, true);
    });
}

function toggleEEL(context: JQuery<HTMLElement>, containerClassname: string, hiddenValueClassname: string)
{
    context.find(containerClassname).each(function ()
    {
        const eelContainer = $(this);
        const eelHiddenValue = eelContainer.find(hiddenValueClassname);
        eelHiddenValue.on("change", function ()
        {
            doProcessDependents(context, eelContainer, true);
        });

        doProcessDependents(context, eelContainer, false, true);
    });
}

function toggleCheckBox(this: JQuery<HTMLElement>)
{
    const context = this;
    context.find("input[type='checkbox']").each(function ()
    {
        const checkbox = $(this);
        checkbox.off("change", processToggleCheckBox);
        checkbox.on("change", processToggleCheckBox);

        doProcessDependents(context, checkbox, false, true);
    });

    function processToggleCheckBox(this: JQuery<HTMLElement>)
    {
        const checkbox = $(this);
        doProcessDependents(context, checkbox, true);
    }
}

function toggleAccountDimensionEEL(this: JQuery<HTMLElement>)
{
    const context = this;
    context.find(".accountDimensionEEL").each(function ()
    {
        const accountDimensionEEL = $(this);
        const accountDimensionEELValue = accountDimensionEEL.children(".accountDimensionEELValue");
        const accountDimensionEELCode = accountDimensionEEL.children(".accountDimensionEELCode");
        accountDimensionEEL.on("change", function ()
        {
            doProcessDependents(context, accountDimensionEEL, true);
            if (accountDimensionEELCode.hasClass('IsFund') && accountDimensionEELValue.val() !== '')
            {
                focusAfter(accountDimensionEELCode);
            }
        });

        doProcessDependents(context, accountDimensionEEL, false, true);
    });
}

function toggleInputs(this: JQuery<HTMLElement>)
{
    const context = this;
    // input[type="text"] also covers new datepickers
    context.find('.datepicker,input[type="text"],input[type="hidden"]').each(function ()
    {
        const input = $(this);
        input.on("change", function ()
        {
            doProcessDependents(context, input, true);
        });
        doProcessDependents(context, input, false, true);
    });
}

function toggleDateTimePickers(this: JQuery<HTMLElement>)
{
    const context = this;
    context.find('.dateTimePickerWrapper').each(function ()
    {
        const dateTimePicker = $(this);
        dateTimePicker.on("change", function (event)
        {
            doProcessDependents(context, $(event.currentTarget), true);
        });
        doProcessDependents(context, dateTimePicker, false, true);
    });
}

function toggleDistinctMultiSelectEEL(this: JQuery<HTMLElement>)
{
    const context = this;
    context.find('.distinctMultiSelectEEL').each(function ()
    {
        const distinctMultiSelectEEL = $(this);
        const hiddenBackingData = distinctMultiSelectEEL.find(".dmsBackingData");
        hiddenBackingData.on("change", function ()
        {
            doProcessDependents(context, distinctMultiSelectEEL, true);
        });
        doProcessDependents(context, distinctMultiSelectEEL, false, true);
    });
}

// Main function to fire off when changing the value of a parent
function doProcessDependents(context: JQuery<HTMLElement>, input: JQuery<HTMLElement>, parentChanged: boolean, isLoading?: boolean)
{
    const control = input.is("option") ? input.parent() : input;
    if (input.hasClass('editableFields') || (parentChanged && control.hasClass("js-disableTriggerOnChange")))
    {
        return;
    }

    const fauxFormLoad = $('#FauxFormLoad');
    if (fauxFormLoad.length > 0)
    {
        isLoading = fauxFormLoad.val() === "true";
    }

    let dependents: DependentsData | undefined = input.data("dependents");

    if (dependents === undefined && input.is("option"))
    {
        dependents = control.data("dependents") as DependentsData | undefined;
    }

    if (dependents)
    {
        const dependentAnnouncement = document.body.querySelector("#screenReaderDependentAnnouncement");
        const dependentAnnouncementId = "screenReaderDependentAnnouncement";

        // Ensure the dependent announcement exists & is announced
        if (!dependentAnnouncement)
        {
            const hiddenAnnouncement = document.createElement("div");
            hiddenAnnouncement.id = dependentAnnouncementId;
            hiddenAnnouncement.className = "sr-only dependentAnnouncement";
            hiddenAnnouncement.setAttribute("aria-live", "polite");

            // Append to the body
            document.body.append(hiddenAnnouncement);

            setTimeout(() => { hiddenAnnouncement.textContent = "Options on this page may change based on your selections."; }, 0)
        }
        else
        {
            dependentAnnouncement.innerHTML = dependentAnnouncement.textContent + "&nbsp";
        }

        // Find the top-most content panel
        let closestContentPanel = getClosestContentPanel(input);

        const popup = closestContentPanel.closest(".popup");
        if (popup.length > 0)
        {
            closestContentPanel = popup;
        }

        const triggerDependency = dependents.triggerOnLoad || (parentChanged && !isLoading);
        if (triggerDependency)
        {
            $.each(dependents.dependents, function ()
            {
                let panel = getClosestContentPanel(context);
                if (panel.length == 0)
                {
                    panel = context;
                }

                let currentDependent = panel.find('[id="' + this.Id + '"]');
                if (currentDependent.length === 0)
                {
                    // TODO: (5270635) check if this is needed
                    currentDependent = panel.find(`[data-prefixed-id="${this.Id}"]`);
                }

                const dependencies = this;

                function doProcessDependentLogic(dependent: JQuery<HTMLElement>, dependentRef: any)
                {
                    if (dependent.hasClass('editableFields'))
                    {
                        return;
                    }

                    //process single dependent
                    let attemptedValueChangeBreak = true;

                    if (!(!isLoading || dependentRef.TriggerOnLoad === 'true') || !(parentChanged || dependencies.TriggerOnLoad === 'true'))
                    {
                        return;
                    }

                    let value = getValue(input);
                    value = (value !== undefined && value !== null) ? value.toString() : "";

                    const onValueArray = (typeof dependencies.OnValue === "string") ? dependencies.OnValue.split(',') : [];
                    const notOnValueArray = (typeof dependencies.NotOnValue === "string") ? dependencies.NotOnValue.split(',') : [];

                    if (dependencies.OnValue !== undefined && !onValueArray.includes(value))
                    {
                        return;
                    }
                    if (dependencies.NotOnValue !== undefined && notOnValueArray.includes(value))
                    {
                        return;
                    }

                    //If we are clearing the dependencies, we don't care what the input is
                    if (dependencies.Clear && dependencies.Clear === "true")
                    {
                        if (input.hasClass('setByWorkflowTemplate'))
                        {
                            input.removeClass('setByWorkflowTemplate');
                        }
                        else
                        {
                            clearDependent(dependent);
                        }
                    }

                    //If the dependent is a checkbox, and we are toggling or clearing, we don't care what the input is
                    if (dependent.is(":checkbox"))
                    {
                        if (dependencies.Toggle === "true")
                        {
                            setCheckboxValue(dependent, !dependent.prop("checked"));
                            attemptedValueChangeBreak = false;
                        }
                        if (dependencies.Clear === "false")
                        {
                            setCheckboxValue(dependent, true);
                            attemptedValueChangeBreak = false;
                        }
                    }

                    //Process the remaining options that require us to know what the value is on the input
                    if ((input.is(":checkbox, input:radio") && input.prop("checked")) || //Checkbox & Radio Button
                        input.is("option") || //Combobox Option
                        (input.is("div.codeEEL") && input.find(".codeEELValue").val()) || //CodeEEL
                        (input.is("div.accountDimensionEEL") && input.find(".accountDimensionEELValue").val()) ||//AccountDimensionEEL
                        (input.is(".distinctMultiSelectEEL") && dmseGetSearchParameters(input.find<HTMLInputElement>('.dmsBackingData')).length > 0) ||
                        // TODO: Remove datepicker in PR 6145635
                        (input.is("input") && input.is(".datepicker,.text,.number,.durationpicker") && input.val() !== "") ||
                        (input.is('input[type="hidden"]') && input.val() !== "") ||
                        (input.is(".dateTimePickerWrapper") && value) ||
                        (input.is("input") && (input.is(".datePicker") || input.is(".timePicker") || input.is(".dateTimePicker")) && value) ||
                        (input.hasClass("tileViewEEL") && input.find(".tileViewEEL_value").val()))
                    {
                        if (dependencies.Visibility)
                        {
                            setVisibility(dependent, dependencies.Visibility === "true", dependencies.AnimateVisibilityChange !== "false");
                        }

                        if (dependencies.ReadOnly)
                        {
                            setReadOnly(dependent, dependencies.ReadOnly === "true");
                            if (dependent.is(":text"))
                            {
                                setTextboxMask(dependent, input.is(":checked"));
                            }
                        }

                        if (dependent.is(":checkbox") && triggerDependency && dependencies.Value)
                        {
                            setCheckboxValue(dependent, typeof (dependencies.Value) === "string" && dependencies.Value.toLowerCase() === "true");
                            attemptedValueChangeBreak = false;
                        }

                        if (dependent.is(":text") && triggerDependency && dependencies.Mask && dependencies.showMask)
                        {
                            setTextboxMask(dependent, dependencies.showMask == input.is(":checked"));
                        }

                        if (triggerDependency && dependencies.ClearErrors)
                        {
                            displayValidationMessages(null, dependencies.ClearErrors);
                        }

                        if (dependent.is(":radio") && triggerDependency && dependencies.Value)
                        {
                            setRadioButtonValue(dependent, dependencies.Value === "true");
                            attemptedValueChangeBreak = false;
                        }
                    }
                    else
                    {
                        if (dependencies.Visibility)
                        {
                            setVisibility(dependent, dependencies.Visibility !== "true");
                        }

                        if (dependencies.ReadOnly)
                        {
                            setReadOnly(dependent, dependencies.ReadOnly !== "true");
                            if (dependent.is(":text"))
                            {
                                setTextboxMask(dependent, input.is(":checked") || dependencies.showMask === true);
                            }
                        }

                        if (dependent.is(":checkbox") && triggerDependency && dependencies.Value)
                        {
                            setCheckboxValue(dependent, dependencies.Value !== "true");
                            attemptedValueChangeBreak = false;
                        }

                        if (dependent.is(":text") && triggerDependency && dependencies.Mask && dependencies.showMask)
                        {
                            setTextboxMask(dependent, dependencies.showMask == input.is(":checked"));
                        }
                    }

                    if (triggerDependency && dependencies.Value && (dependent.is(":text") || dependent.is(".dateTimePickerWrapper") || dependent.hasClass("radioSet") || dependent.hasClass("comboBoxSet") || dependent.hasClass("codeEEL"))) // TODO This is flawed logic. A radio button with the value of "false" cannot be set by this code. Also, it fails to honor OnValue.
                    {
                        setValue(dependent, dependencies.Value);
                        attemptedValueChangeBreak = false;
                    }
                    else if (triggerDependency && dependent.hasClass("radioButtonGroup") && dependencies.Value !== undefined &&
                        (dependencies.OnValue === undefined || onValueArray.includes(value)))
                    {
                        radioButtonGroupSetValue(dependent.find(".radioButtonGroupValue"), dependencies.Value);
                        attemptedValueChangeBreak = false;
                    }

                    if (dependencies.CopyParentValue)
                    {
                        setDependentValue(dependent, getValue(input));
                    }
                    else if (dependencies.DependentValue)
                    {
                        setDependentValue(dependent, dependencies.DependentValue);
                    }

                    if (dependent.is(":input"))
                    {
                        if (dependencies.Placeholder)
                        {
                            dependent.attr('placeholder', dependencies.Placeholder);
                        }
                    }

                    // Used by readonly browses and by filterable controls.
                    // Currently, Editable Browse dependency works differently than regular browses
                    if (dependencies.Refresh === "true")
                    {
                        dependent.trigger("refresh");
                    }

                    // Dependent Value takes precedence over a Value Function
                    if (!dependencies.DependentValue && dependencies.ValueFunction)
                    {
                        if (dependencies.ValueFunctionArgs === undefined)
                        {
                            dependencies.ValueFunctionArgs = '';
                        }

                        applyValueFunction(dependent, dependencies.ValueFunction, dependencies.ValueFunctionArgs);
                    }

                    if (valueHasChanged(dependent))
                    {
                        if (!attemptedValueChangeBreak)
                        {
                            doProcessDependents(context, dependent, true, isLoading);
                        }
                    }
                };

                //TODO @ME This is not valid logic
                // this determines if the current dependent is a normal dependent or part of the Incident Quick Entry repeater
                if (currentDependent.length !== 0)
                {
                    doProcessDependentLogic(currentDependent, this);
                }
                else
                {
                    const quickEntryDependents = $('.disciplineQuickEntryColumn [id^="' + this.Id + '-add-"]');
                    if (quickEntryDependents.length === 1)
                    {
                        doProcessDependentLogic(quickEntryDependents, this);
                    }
                    else if (quickEntryDependents.length > 1)
                    {
                        quickEntryDependents.each(function ()
                        {
                            doProcessDependentLogic($(this), this);
                        });
                    }
                    else
                    {
                        if (panel.is(".workflow"))
                        {
                            const popupId = panel.closest(".popup").attr("id")!;
                            const idToSearch = `${popupId}_${this.Id}`;
                            currentDependent = panel.find(`[id="${idToSearch}"]`);
                        }

                        if (currentDependent.length !== 0)
                        {
                            doProcessDependentLogic(currentDependent, this);
                        }
                        else
                        {
                            //console.log(`Could not find dependent with ID ${this.Id} for element`, input);
                        }
                    }
                }
            });
        }
        notifyResize();
    }
    else if (input.hasClass("setByWorkflowTemplate"))
    {
        input.removeClass("setByWorkflowTemplate");
    }
}

/**
 * Select the visible tab.
 * When switching ui components tabs maybe hidden, so we need to ensure the first visible tab is selected.
 */
function toggleTab(this: JQuery<HTMLElement>)
{
    this.find(".tabContainer").each(function ()
    {
        const container = $(this);
        const tabButtonList = container.find(".tabStrip .tabButton");
        const tabContentsList = container.find(".tabContents");

        tabButtonList.each(function (index)
        {
            const button = $(this);
            const contents = $(tabContentsList[index]);

            if (button.is(":visible"))
            {
                tabButtonList.removeClass("current");
                button.addClass("current");
                tabContentsList.removeClass("current");
                contents.addClass("current");
                return false;
            }
            return;
        });
    });
}

/**
 * Function to clear a dependent control
 */
function clearDependent(dependent: JQuery<HTMLElement>)
{
    if (dependent.hasClass('setByWorkflowTemplate'))
    {
        dependent.removeClass('setByWorkflowTemplate');
        return;
    }

    if (dependent.hasClass('editableFields'))
    {
        return;
    }

    if (dependent.hasClass("DefaultComboBoxSet"))
    {
        return;
    }

    if (dependent.hasClass("standardFilterViewer"))
    {
        standardFilterViewerClear(dependent);
    }
    else if (dependent.is(":checkbox"))
    {
        setCheckboxValue(dependent, false);
    }
    else if (dependent.hasClass("checkboxGroup"))
    {
        checkboxGroupToggleAll(dependent, false);
    }
    else if (dependent.is(".radioButtonGroupValue"))
    {
        dependent.closest(".radioButtonGroup").find("[type='radio']").prop("checked", false);
        setValue(dependent, null);
    }
    else if (dependent.is(":radio"))
    {
        dependent.prop("checked", false);
    }
    else if (dependent.is("div.codeEEL"))
    {
        const suppressChangeEvent = dependent.find(".suppressChangeEvent").length > 0;
        clearValue(dependent, suppressChangeEvent);
    }
    else if (dependent.hasClass("tileViewEEL") || dependent.hasClass("dateTimePickerWrapper"))
    {
        clearValue(dependent);
    }
    else if (dependent.is(".distinctMultiSelectEEL"))
    {
        dependent.children(".selectedItems").find(".selectedItem:not(.dummyTile)").remove();
        clearValue(dependent);
    }
    else if (dependent.hasClass("multipleSelectBox"))
    {
        dependent.children().each(function ()
        {
            const element = $(this);
            if (element.hasClass("multiSelectBackingData"))
            {
                element.data("searchParameters").length = 0;
                element.trigger("change");
            }
            else if (element.hasClass("multipleSelectOption"))
            {
                const checkbox = element.find(".multipleSelectOptionCheckbox");
                checkbox.prop("checked", false);
            }
        });
    }
    else if (dependent.hasClass("filterContainer"))
    {
        const filterBuilderLaunchButton = dependent.find(".filterBuilderLaunchButton");
        skyward.filterbuilder.updateFilter(filterBuilderLaunchButton, null);
    }
    else if (isHtmlEditorElement(dependent))
    {
        htmlEditorClearValue(dependent);
    }
    else if (dependent.is("div") || dependent.is("fieldset"))
    {
        if (dependent.hasClass("setByWorkflowTemplate"))
        {
            dependent.removeClass("setByWorkflowTemplate");
        }
        else
        {
            //The dependent is a container, so clear all of the child controls
            dependent.children().each(function (this: HTMLElement)
            {
                clearDependent($(this));
            });
        }
    }
    else if (dependent.is("input") || dependent.is("textarea") || dependent.is("select"))
    {
        setValue(dependent, null);
    }
    else
    {
        dependent.val(null as unknown as any);
    }
}

/**
 * Change the visibility of an input
 */
function setVisibility(input: JQuery<HTMLElement>, value: boolean, animate?: boolean)
{
    if (input && !input.hasClass("tabContents"))
    {
        toggleVisible(input, value, animate);
    }
}

/**
 * Change the readonly/disabled state of an input
 * @param element
 * @param isReadOnly
 */
function setReadOnly(element: JQuery<HTMLElement>, isReadOnly: boolean)
{
    if (!toggle(element, !isReadOnly) && element.is("div,fieldset"))
    {
        //Update all of the child controls
        element.children().each(function ()
        {
            setReadOnly($(this), isReadOnly);
        });
    }
}

/**
 * Set a checkbox value
 * @param checkbox
 * @param value
 */
function setCheckboxValue(checkbox: JQuery<HTMLElement>, value: boolean)
{
    if (checkbox.is(":checkbox"))
    {
        checkbox.prop("checked", value);
        checkbox.trigger("change");
    }
}

function setRadioButtonValue(radioButton: JQuery<HTMLElement>, value: boolean)
{
    if (radioButton.is(":radio"))
    {
        radioButton.prop("checked", value);
        radioButton.trigger("change");
    }
}

function setTextboxMask(textbox: JQuery<HTMLElement>, showMask: boolean)
{
    const textboxData = textbox.data();
    if (showMask)
    {
        if (textboxData.decimalMask != null)
        {
            addDecimalMask(textbox, textbox.data().decimalMask.toString());
        }
        else if (textboxData.mask != null)
        {
            addMask(textbox, textbox.data().mask);
        }

    }
    else
    {
        if (textboxData.mask || textboxData.decimalMask)
        {
            removeMask(textbox);
        }
    }
}

function setDependentChildrenValues(dependent: JQuery<HTMLElement>, childSelector: string, value: any)
{
    dependent.children(childSelector).each(function (this: HTMLElement)
    {
        $(this).val(value);
    });
}

function setDependentValue(dependent: JQuery<HTMLElement>, value: unknown)
{
    if (dependent.hasClass("DefaultComboBoxSet"))
    {
        return;
    }

    if (dependent.is(":checkbox"))
    {
        setCheckboxValue(dependent, value as boolean);
    }
    else if (dependent.is("div.codeEEL"))
    {
        setDependentChildrenValues(dependent, ".codeEELValue, .codeEELCode, .codeEELDescription", value);
    }
    else if (dependent.hasClass("tileViewEEL"))
    {
        setDependentChildrenValues(dependent, ".tileViewEEL_value, .tileViewEEL_search", value);
    }
    else if (dependent.is("label") || dependent.hasClass("visualLabel"))
    {
        dependent.text(value as string);
        alignTitles("#" + dependent.attr('id'));
    }
    else
    {
        const maxLength: number = dependent.prop("maxLength");
        if (maxLength !== -1 && !isNaN(maxLength) && typeof value === "string" && value.length > maxLength)
        {
            value = value.substring(0, maxLength);
        }

        setValue(dependent, value);

        if (dependent.is("select") || dependent.is("input"))
        {
            dependent.trigger("change");
        }

        if (dependent.hasClass('datepicker'))
        {
            skyward.datepicker.setDatePicker(dependent);
        }
    }
}

function applyValueFunction(dependent: JQuery<HTMLElement>, valueFunction: string, valueFunctionArgs: string)
{
    const updateEvent = "keyup";
    let functionToUse = getActionFunction(valueFunction)!;

    if (functionToUse === undefined)
    {
        const functionParts = valueFunction.split(".");

        functionToUse = (window as Record<string, any>)[functionParts[0]];

        for (let part = 1; part < functionParts.length; part++)
        {
            functionToUse = (functionToUse as any)[functionParts[part]];
        }
    }

    const argArray = valueFunctionArgs.split(",");
    const elements = argArray.filter(arg => arg.charAt(0) === "#").map((e) => $(e));

    if (elements.length > 0)
    {
        for (const jqThis of elements)
        {
            const attributeName = 'data-example-codependent-' + dependent.attr('id') + '-' + valueFunction + '-' + valueFunctionArgs.split('#').join('').split(',').join('-') + '-bound';
            if (jqThis.attr(attributeName) === undefined)
            {
                jqThis.debounce(updateEvent, function ()
                {
                    const newArgsArray = argArray.map(arg => arg.charAt(0) === "#" ? $(arg).val() : arg);

                    setDependentValue(dependent, functionToUse.apply(null, newArgsArray));
                });

                jqThis.attr(attributeName, 'true');
            }
        }
    }
    else
    {
        setDependentValue(dependent, functionToUse.apply(null, arguments));
    }

    // Trigger event to update
    if (elements[0])
    {
        elements[0].trigger(updateEvent);
    }
}
